/**
 * Aspect — Revive ID pass (object-ID mask render for exact silhouettes)
 * --------------------------------------------------------------
 * Renders the scene to a small offscreen target with every target furniture
 * entity in a unique flat color and all structure (walls, floors, doors) in
 * flat black, then reads the pixels back and splits them into per-item binary
 * masks. Occlusion comes from the depth buffer for free — an item half-hidden
 * behind a couch only masks its visible pixels.
 *
 * Robustness details learned the hard way:
 *   - The render pipeline may tonemap / gamma-encode / expose the flat colors,
 *     so IDs are encoded as 3-LEVEL channel trits (0 / 128 / 255). Level
 *     thresholds survive any monotonic color transform, unlike exact matches.
 *     26 ids fit per pass (3³ − background); more items = more passes.
 *   - GPU readback rows are bottom-up: the classification write flips Y so
 *     masks line up with screen space.
 *   - Every swapped material and camera/scene flag restores in `finally` — a
 *     failed readback can never leave the scene flat-colored.
 *
 * Engine-API only (Texture / RenderTarget / StandardMaterial / layers).
 */

import * as pc from "playcanvas";

/** Mask render width in px (height follows the canvas aspect). */
const ID_RT_WIDTH = 640;
/** Max items that get a silhouette (largest on screen first; rest keep rects). */
const MAX_ID_ITEMS = 40;
/** Ids per render pass: 3 levels per RGB channel minus the black background. */
const IDS_PER_PASS = 26;

const TRIT_VALUE = [0, 128, 255];

/** Local index (0..25) → trit triple color (skips 0,0,0 = background). */
function tritColor(localIndex) {
  const t = localIndex + 1;
  return {
    r: TRIT_VALUE[t % 3],
    g: TRIT_VALUE[((t / 3) | 0) % 3],
    b: TRIT_VALUE[((t / 9) | 0) % 3],
  };
}

/**
 * Read-back channel value → trit level, using thresholds calibrated to where
 * the pipeline actually lands the 128 mid-level (56 in a linear-output
 * pipeline, ~186 in a gamma-encoding one). Low is always ~0, high ~255.
 */
function makeLevelFn(midAnchor) {
  const lo = midAnchor / 2;
  const hi = (midAnchor + 255) / 2;
  return (v) => (v < lo ? 0 : v > hi ? 2 : 1);
}

/**
 * Find where the mid trit (128) landed in this readback: the median of all
 * channel values that are clearly neither black nor full white.
 */
function calibrateMid(pixels) {
  const mids = [];
  for (let p4 = 0; p4 < pixels.length && mids.length < 4096; p4 += 16) {
    for (let c = 0; c < 3; c++) {
      const v = pixels[p4 + c];
      if (v > 12 && v < 243) mids.push(v);
    }
  }
  if (mids.length < 32) return 56; // no mid-bearing colors visible this pass
  mids.sort((a, b) => a - b);
  return mids[(mids.length / 2) | 0];
}

/**
 * Render the ID masks and classify pixels per item.
 *
 * @param {import('playcanvas').AppBase} app
 * @param {pc.Entity} mainCamera  the live view camera (pose is cloned)
 * @param {Map<string, pc.Entity>} targets  placement id -> entity (enabled only)
 * @param {pc.Entity[]} occluderRoots  subtrees rendered flat black (room shell)
 * @returns {Promise<{ width:number, height:number, maskOf:(id:string)=>Uint8Array|null }|null>}
 */
export async function captureIdMasks(app, mainCamera, targets, occluderRoots = []) {
  const device = app.graphicsDevice;
  const canvas = device.canvas;
  const aspect = (canvas.clientWidth || 16) / (canvas.clientHeight || 9);
  const w = ID_RT_WIDTH;
  const h = Math.max(64, Math.round(w / aspect));

  // Feature-gate: async texture readback. Without it, rect hitboxes remain.
  if (typeof pc.Texture.prototype.read !== "function") return null;

  const colorBuffer = new pc.Texture(device, {
    width: w,
    height: h,
    format: pc.PIXELFORMAT_RGBA8,
    mipmaps: false,
    minFilter: pc.FILTER_NEAREST,
    magFilter: pc.FILTER_NEAREST,
  });
  const rt = new pc.RenderTarget({ colorBuffer, depth: true });

  const camEnt = new pc.Entity("revive-id-camera");
  camEnt.addComponent("camera", {
    clearColor: new pc.Color(0, 0, 0, 1),
    layers: [pc.LAYERID_WORLD],
    renderTarget: rt,
    fov: mainCamera.camera.fov,
    nearClip: mainCamera.camera.nearClip,
    farClip: mainCamera.camera.farClip,
  });
  // Ask the engine for the flattest pipeline it supports; the trit thresholds
  // cope even when these knobs don't exist on older versions.
  try {
    if (pc.TONEMAP_NONE !== undefined) camEnt.camera.toneMapping = pc.TONEMAP_NONE;
    else camEnt.camera.toneMapping = pc.TONEMAP_LINEAR;
  } catch {
    /* per-camera tonemap unsupported */
  }
  try {
    if (pc.GAMMA_NONE !== undefined) camEnt.camera.gammaCorrection = pc.GAMMA_NONE;
  } catch {
    /* per-camera gamma unsupported */
  }

  const swapped = []; // { mi, material } for exact restore
  const flatMats = [];

  /** One reusable flat material per painted subtree (color set per pass). */
  const paint = (entity, color, opts = {}) => {
    const mat = new pc.StandardMaterial();
    mat.diffuse = new pc.Color(0, 0, 0);
    mat.specular = new pc.Color(0, 0, 0);
    mat.emissive = new pc.Color(color.r / 255, color.g / 255, color.b / 255);
    mat.useFog = false;
    mat.depthWrite = true;
    mat.depthTest = true;
    // Interior wall faces are often the back of a single-sided mesh. Without
    // this, the room paints nothing and furniture through the wall gets an id.
    if (opts.doubleSide && pc.CULLFACE_NONE != null) mat.cull = pc.CULLFACE_NONE;
    mat.update();
    flatMats.push(mat);
    for (const render of entity.findComponents("render")) {
      for (const mi of render.meshInstances) {
        swapped.push({ mi, material: mi.material });
        mi.material = mat;
      }
    }
    return mat;
  };

  const ids = [...targets.keys()].slice(0, MAX_ID_ITEMS);
  const passes = Math.ceil(ids.length / IDS_PER_PASS);
  const index = new Int16Array(w * h).fill(-1); // final: pixel -> ids[] index
  const mainCamWasEnabled = mainCamera.camera.enabled;
  const prevExposure = app.scene.exposure;

  try {
    // Paint everything black once; per pass we recolor just that pass's chunk.
    const matOf = new Map(); // placement id -> its flat material
    for (const id of ids) matOf.set(id, paint(targets.get(id), { r: 0, g: 0, b: 0 }));
    for (const root of occluderRoots) if (root) paint(root, { r: 0, g: 0, b: 0 }, { doubleSide: true });

    camEnt.setPosition(mainCamera.getPosition());
    camEnt.setRotation(mainCamera.getRotation());
    app.root.addChild(camEnt);
    mainCamera.camera.enabled = false; // the canvas keeps its last beauty frame
    app.scene.exposure = 1;

    for (let pass = 0; pass < passes; pass++) {
      const chunk = ids.slice(pass * IDS_PER_PASS, (pass + 1) * IDS_PER_PASS);

      // Recolor: this pass's chunk gets trit colors, everything else black.
      ids.forEach((id, i) => {
        const mat = matOf.get(id);
        const local = i - pass * IDS_PER_PASS;
        if (local >= 0 && local < chunk.length) {
          const c = tritColor(local);
          mat.emissive.set(c.r / 255, c.g / 255, c.b / 255);
        } else {
          mat.emissive.set(0, 0, 0);
        }
        mat.update();
      });

      await new Promise((resolve) => {
        app.once("frameend", resolve);
        app.renderNextFrame = true;
      });
      const pixels = await colorBuffer.read(0, 0, w, h);
      const level = makeLevelFn(calibrateMid(pixels));

      for (let y = 0; y < h; y++) {
        // GPU readback is bottom-up — flip into screen orientation here.
        const srcRow = y * w;
        const dstRow = (h - 1 - y) * w;
        for (let x = 0; x < w; x++) {
          const p4 = (srcRow + x) * 4;
          const t =
            level(pixels[p4]) + level(pixels[p4 + 1]) * 3 + level(pixels[p4 + 2]) * 9;
          if (t === 0) continue; // background / occluder / other-pass items
          const local = t - 1;
          if (local >= chunk.length) continue; // unused code point (AA blend)
          index[dstRow + x] = pass * IDS_PER_PASS + local;
        }
      }
    }

    return {
      width: w,
      height: h,
      maskOf(id) {
        const i = ids.indexOf(id);
        if (i < 0) return null;
        const mask = new Uint8Array(w * h);
        let any = false;
        for (let p = 0; p < w * h; p++) {
          if (index[p] === i) {
            mask[p] = 1;
            any = true;
          }
        }
        return any ? mask : null;
      },
    };
  } catch (err) {
    console.warn("[revive] ID pass failed — keeping rectangle hitboxes", err);
    return null;
  } finally {
    for (const { mi, material } of swapped) mi.material = material;
    mainCamera.camera.enabled = mainCamWasEnabled;
    app.scene.exposure = prevExposure;
    camEnt.destroy();
    rt.destroy();
    colorBuffer.destroy();
    for (const mat of flatMats) mat.destroy();
  }
}

export { MAX_ID_ITEMS };
export default captureIdMasks;
